import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import helpers from 'helpers';

import DropdownField from '../../widgets/DropdownField';
import Button from '@xanadu/ui-shared/components/widgets/Button';
import { IS_MOBILE, MONTHLY_INCOME_RANGE, OCCUPATION_LIST } from '../../../constants';

import ExternalLink from '../../widgets/ExternalLink';

import styles from './OccupationSalaryRange.module.scss';
import { DOCUMENT_STATUSES } from '@xanadu/ui-shared/modules/user/data';

const currencyFormatter = helpers.localise.currency;

const INITIAL_FIELD = {
  income: '',
  occupation: ''
};

const { APPROVED } = DOCUMENT_STATUSES;

const OccupationSalaryRange = ({
  logout,
  updateOccupationSalaryRange,
  isLoading,
  errorMessage,
  proofOfAddressStatus,
  proofOfIdentityStatus,
  jobTitle,
  salaryRange,
  closeModal,
}) => {
  const [fields, setFields] = useState(INITIAL_FIELD);

  const useSubmitButton = proofOfAddressStatus === APPROVED && proofOfIdentityStatus === APPROVED;

  const appendCurrency = (amount) => {
    const options = { maximumFractionDigits: 0 };
    return currencyFormatter(amount, options);
  };

  const COMPUTED_SALARY_RANGE = MONTHLY_INCOME_RANGE.map((item, index) => ({
    id: index + 1,
    value: `${item.start} ${item.end ? `- ${item.end}` : '+'}`,
    name: `${appendCurrency(item.start)} ${item.end ? `- ${appendCurrency(item.end)}` : '+'}`
  }));

  useEffect(() => {
    setFields({
      occupation: jobTitle || '',
      income: salaryRange || '',
    });
  }, [jobTitle, salaryRange]);

  const onInputChange = (result) => {
    const { name, value } = result;
    setFields((prevState) => ({
      ...prevState,
      [name]: value.name,
    }));
  };

  const handleSubmission = () => {
    const payload = {
      salaryRange: fields.income,
      jobTitle: fields.occupation
    };
    if (!salaryRange) {
      payload.salaryRange = COMPUTED_SALARY_RANGE.find(income => income.name === fields.income).value || '';
    }
    updateOccupationSalaryRange(payload, { showDocumentUpload: !useSubmitButton });
  };

  const handleLogout = () => {
    logout();
    closeModal();
  };

  const disabled = (!fields.income || !fields.occupation) || isLoading;

  return (
    <div className={styles.uploadDocuments}>
      <div>
        <div className={styles.textContainer}>
          <div className={styles.textTop}>
            Occupation &amp; Income
          </div>
        </div>
        <div className={styles.uploadContainer}>
          <p className={styles.topText}>
            {"As per UK Gambling Commission's guidance, we must now ask our customers to provide details of their occupation and/or monthly income."}
          </p>
          <div className={styles.inputGroup}>
            {
              !jobTitle && (
                <DropdownField
                  fluid={IS_MOBILE}
                  label="Occupation"
                  name="occupation"
                  theme="dark"
                  textAlign="left"
                  items={OCCUPATION_LIST}
                  value={fields.occupation}
                  onPropertyChange={onInputChange}
                  placeholder="Select an option"
                />
              )
            }
            {
              !salaryRange && (
                <DropdownField
                  fluid={IS_MOBILE}
                  label="Monthly Net Income"
                  name="income"
                  theme="dark"
                  textAlign="left"
                  items={COMPUTED_SALARY_RANGE}
                  value={fields.income}
                  onPropertyChange={onInputChange}
                  placeholder="Select an option"
                />
              )
            }
          </div>
          {
            errorMessage && (
              <p className={styles.errorText}>{errorMessage}</p>
            )
          }
          <p className={styles.bottomText}>
            {'In the future you may be asked for documention to support this information. We will contact you if this is required.'}<br /><br />
            <ExternalLink href={'https://www.matchbook.com/page/faqs/know-your-customer-requirements'} content={'Why do we ask for this?'} />
          </p>
        </div>
      </div>
      <div className={styles.bottomSection}>
        <div className={styles.buttons}>
          {
            useSubmitButton && (
              <Button
                sizeType="wider"
                buttonType="flat"
                onClick={handleLogout}
                disabled={false}
                loading={isLoading}
              >Close &amp; Logout</Button>
            )
          }
          <Button
            sizeType="wider"
            buttonType="primary"
            onClick={handleSubmission}
            disabled={disabled}
            loading={isLoading}
            style={{ marginLeft: 16 }}
          >{useSubmitButton ? 'Submit' : 'NEXT - UPLOAD DOCUMENTS'}</Button>
        </div>
      </div>
    </div>
  );
};

OccupationSalaryRange.propTypes = {
  jobTitle: PropTypes.string,
  salaryRange: PropTypes.string,
  proofOfIdentityStatus: PropTypes.string,
  proofOfAddressStatus: PropTypes.string,
  isLoading: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string,
  updateOccupationSalaryRange: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
};

export default OccupationSalaryRange;
